import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { adminService } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { usePrompt } from '../../contexts/PromptContext';
import { Card, Badge, Button, EmptyState, Spinner, Pagination } from '../../components/ui';

const PAGE_SIZE = 20;

function AdminTeacherApprovals() {
  const { addNotification } = useNotification();
  const promptReason = usePrompt();
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const fetchProfiles = () => {
    setLoading(true);
    adminService.getTeacherProfiles({ status: filter || undefined, page, page_size: PAGE_SIZE })
      .then((res) => {
        const data = res.data;
        setProfiles(Array.isArray(data) ? data : data.results || []);
        setCount(Array.isArray(data) ? data.length : data.count || 0);
      })
      .catch(() => addNotification(t('Failed to load teacher profiles'), 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(fetchProfiles, [filter, page]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setSelected([]); }, [filter]);

  const pendingSelectable = profiles.filter((p) => p.status !== 'approved');
  const allSelected = pendingSelectable.length > 0 && selected.length === pendingSelectable.length;

  const toggleSelectAll = () => {
    setSelected(allSelected ? [] : pendingSelectable.map((p) => p.id));
  };

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleBulkApprove = async () => {
    if (selected.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await adminService.bulkApproveTeacherProfiles(selected);
      addNotification(t('{{count}} teachers approved', { count: res.data.approved }), 'success');
      setSelected([]);
      fetchProfiles();
    } catch (e) {
      addNotification(t('Action failed'), 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const act = async (id, action) => {
    let reason = null;
    if (action === 'suspend') {
      reason = await promptReason(t('Why are you suspending this teacher?'));
      if (!reason) return;
    }
    setBusyId(id);
    try {
      const res = action === 'approve'
        ? await adminService.approveTeacherProfile(id)
        : await adminService.suspendTeacherProfile(id, reason);
      setProfiles((prev) => prev.map((p) => (p.id === id ? res.data : p)));
      addNotification(action === 'approve' ? t('Teacher approved') : t('Teacher suspended'), 'success');
    } catch (e) {
      addNotification(t('Action failed'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const filterLabels = {
    pending: t('Pending'),
    approved: t('Approved'),
    suspended: t('Suspended'),
    '': t('All'),
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Teacher Approvals')} subtitle={t('Review and approve teacher accounts')} />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex gap-2">
              {['pending', 'approved', 'suspended', ''].map((s) => (
                <button
                  key={s || 'all'}
                  onClick={() => { setFilter(s); setPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${filter === s ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'}`}
                >
                  {filterLabels[s]}
                </button>
              ))}
            </div>
            {selected.length > 0 && (
              <Button size="sm" variant="success" loading={bulkBusy} onClick={handleBulkApprove}>
                {t('Approve {{count}} selected', { count: selected.length })}
              </Button>
            )}
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : profiles.length === 0 ? (
            <Card><EmptyState title={t('Nothing here')} description={t('No teacher profiles match this filter.')} /></Card>
          ) : (
            <div className="space-y-3">
              {pendingSelectable.length > 0 && (
                <label className="flex items-center gap-2 text-sm text-gray-600 px-1">
                  <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="h-4 w-4 text-primary-600 border-gray-300 rounded" />
                  {t('Select all pending')}
                </label>
              )}
              {profiles.map((p) => (
                <Card key={p.id} className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-start gap-3">
                    {p.status !== 'approved' && (
                      <input
                        type="checkbox"
                        checked={selected.includes(p.id)}
                        onChange={() => toggleSelect(p.id)}
                        className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded"
                      />
                    )}
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">{p.user?.first_name} {p.user?.last_name}</p>
                        <Badge status={p.status}>{filterLabels[p.status]}</Badge>
                      </div>
                      <p className="text-sm text-gray-500">{p.user?.email}</p>
                      {p.bio && <p className="text-sm text-gray-600 mt-2 max-w-xl">{p.bio}</p>}
                      {p.qualifications && <p className="text-xs text-gray-400 mt-1">{p.qualifications}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {p.status !== 'approved' && (
                      <Button size="sm" variant="success" loading={busyId === p.id} onClick={() => act(p.id, 'approve')}>
                        {t('Approve')}
                      </Button>
                    )}
                    {p.status !== 'suspended' && (
                      <Button size="sm" variant="danger" loading={busyId === p.id} onClick={() => act(p.id, 'suspend')}>
                        {t('Suspend')}
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
          <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} className="mt-4 rounded-xl border bg-white" />
        </main>
      </div>
    </div>
  );
}

export default AdminTeacherApprovals;
